/**
 * Production fix for marks-pane (epub.js underline/highlight renderer).
 *
 * Root cause (marks-pane@1.0.9 Mark.filteredRanges):
 *   Range.getClientRects() for selections that cross block elements often
 *   returns BOTH per-line text boxes AND larger parent/block boxes (the
 *   paragraph's full content box). marks-pane then DROPS any rect contained
 *   by another rect — i.e. it keeps the big paragraph box and discards every
 *   line box inside it.
 *
 * Visible symptom for underlines:
 *   - Multi-line paragraphs only show an underline on the last line
 *     (drawn at the bottom of the kept block box).
 *   - That "last line" underline stretches to the full content width
 *     (block box width), past the actual text.
 *
 * Documented API (epub.js): rendition.annotations.add("underline", cfiRange, …)
 * is correct. Drawing is delegated to marks-pane Underline, which calls
 * filteredRanges() then paints an SVG <line> along each rect's bottom edge.
 *
 * Upstream marks master replaced contains-filtering with JSON dedupe only,
 * which still leaves the spurious block boxes. The correct polarity is:
 * drop containers, keep the leaf/line boxes — preferably by measuring text
 * nodes so parent boxes never appear.
 *
 * This module monkey-patches Mark.prototype.filteredRanges on the shared
 * marks-pane module instance before any EPUB rendition is created.
 */

import { Mark } from "marks-pane";

const EPS = 0.5;

function rectKey(r) {
  return [
    Math.round(r.left * 100) / 100,
    Math.round(r.top * 100) / 100,
    Math.round(r.width * 100) / 100,
    Math.round(r.height * 100) / 100,
  ].join(",");
}

function isNonEmptyRect(r) {
  return r && r.width > EPS && r.height > EPS;
}

/** True when `outer` strictly contains `inner` (not merely equal). */
function strictlyContains(outer, inner) {
  return (
    inner.left >= outer.left - EPS &&
    inner.top >= outer.top - EPS &&
    inner.right <= outer.right + EPS &&
    inner.bottom <= outer.bottom + EPS &&
    (outer.width > inner.width + EPS || outer.height > inner.height + EPS)
  );
}

/**
 * Drop parent/block boxes that fully contain smaller line boxes.
 * Keeps the line-level rects used for accurate underline strokes.
 */
export function dropContainingRects(rects) {
  if (!Array.isArray(rects) || rects.length <= 1) return rects || [];

  return rects.filter((box, i) => {
    for (let j = 0; j < rects.length; j += 1) {
      if (i === j) continue;
      if (strictlyContains(box, rects[j])) {
        return false;
      }
    }
    return true;
  });
}

function dedupeRects(rects) {
  const seen = new Set();
  const out = [];
  for (const r of rects) {
    const key = rectKey(r);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
}

/**
 * Measure each intersecting text node. Avoids block-level parent rects that
 * browsers append when a Range crosses element boundaries (Chrome/WebKit).
 */
export function getTextNodeClientRects(range) {
  if (!range || range.collapsed) return [];

  const doc = range.startContainer?.ownerDocument;
  if (!doc?.createTreeWalker || typeof range.comparePoint !== "function") {
    return Array.from(range.getClientRects() || []).filter(isNonEmptyRect);
  }

  const root =
    range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentNode;

  if (!root) {
    return Array.from(range.getClientRects() || []).filter(isNonEmptyRect);
  }

  const rects = [];
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodeRange = doc.createRange();
  let node = walker.nextNode();

  while (node) {
    const length = node.nodeValue?.length || 0;
    if (length === 0) {
      node = walker.nextNode();
      continue;
    }

    let startOff = 0;
    let endOff = length;

    try {
      // Entire node before range start, or after range end.
      if (range.comparePoint(node, length) < 0) {
        node = walker.nextNode();
        continue;
      }
      if (range.comparePoint(node, 0) > 0) {
        node = walker.nextNode();
        continue;
      }

      if (range.startContainer === node) {
        startOff = Math.min(Math.max(range.startOffset, 0), length);
      }
      if (range.endContainer === node) {
        endOff = Math.min(Math.max(range.endOffset, 0), length);
      }
    } catch {
      let intersects = false;
      try {
        intersects = range.intersectsNode(node);
      } catch {
        intersects = false;
      }
      if (!intersects) {
        node = walker.nextNode();
        continue;
      }
      startOff = range.startContainer === node ? range.startOffset : 0;
      endOff = range.endContainer === node ? range.endOffset : length;
    }

    if (startOff >= endOff) {
      node = walker.nextNode();
      continue;
    }

    try {
      nodeRange.setStart(node, startOff);
      nodeRange.setEnd(node, endOff);
      const clientRects = nodeRange.getClientRects();
      for (let i = 0; i < clientRects.length; i += 1) {
        const r = clientRects[i];
        if (isNonEmptyRect(r)) {
          rects.push(r);
        }
      }
    } catch {
      // Ignore nodes that cannot form a live range (detached, etc.).
    }

    node = walker.nextNode();
  }

  return rects;
}

/**
 * Stable client-rect list for annotation painting.
 */
export function getAnnotationClientRects(range) {
  if (!range) return [];

  let rects = getTextNodeClientRects(range);
  if (rects.length === 0) {
    rects = Array.from(range.getClientRects() || []).filter(isNonEmptyRect);
  }

  return dropContainingRects(dedupeRects(rects));
}

let patched = false;

export function patchMarksPaneFilteredRanges() {
  if (patched) return;
  if (!Mark?.prototype) {
    console.warn(
      "[marks-pane] Mark prototype unavailable; underline rect patch skipped",
    );
    return;
  }

  Mark.prototype.filteredRanges = function filteredRanges() {
    return getAnnotationClientRects(this.range);
  };

  patched = true;
}

// Apply on import so any later epub.js / marks-pane usage is covered.
patchMarksPaneFilteredRanges();

export default patchMarksPaneFilteredRanges;
